import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { STRINGS } from "@/i18n";
import type { ProviderFilterId } from "@/utils/providerConditions";

interface ProviderFiltersProps {
  /** Filters that can change the current list, in display order. */
  available: ProviderFilterId[];
  active: ReadonlySet<ProviderFilterId>;
  onToggle: (filter: ProviderFilterId) => void;
  onClear: () => void;
  shownCount: number;
  totalCount: number;
}

/**
 * Filters combine as AND, using a checkbox dropdown on mobile and buttons on desktop.
 */
export function ProviderFilters({
  available,
  active,
  onToggle,
  onClear,
  shownCount,
  totalCount,
}: ProviderFiltersProps) {
  const t = STRINGS.providers;
  if (available.length === 0) return null;
  const activeCount = available.filter((filter) => active.has(filter)).length;

  return (
    <div className="border-b border-border px-4 py-3 md:px-6">
      <div className="flex flex-col gap-2 md:hidden">
        <span id="mobile-filter-label" className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {t.filterTitle}
        </span>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              aria-labelledby="mobile-filter-label mobile-filter-value"
              className="h-auto min-h-11 w-full justify-between bg-card text-base"
            >
              <span id="mobile-filter-value" className="truncate">
                {activeCount === 0
                  ? t.allFilters
                  : available.filter((filter) => active.has(filter)).map((filter) => t.filters[filter]).join(", ")}
              </span>
              <ChevronDown className="shrink-0 text-muted-foreground" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            aria-label={t.filterTitle}
            className="w-[var(--radix-popover-trigger-width)] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-1 md:hidden"
          >
            <label className="flex min-h-11 cursor-pointer items-center gap-3 rounded-md px-3 hover:bg-muted">
              <input type="checkbox" checked={activeCount === 0} onChange={onClear} className="size-4 accent-primary" />
              {t.allFilters}
            </label>
            {available.map((filter) => (
              <label key={filter} className="flex min-h-11 cursor-pointer items-center gap-3 rounded-md px-3 hover:bg-muted">
                <input type="checkbox" checked={active.has(filter)} onChange={() => onToggle(filter)} className="size-4 accent-primary" />
                {t.filters[filter]}
              </label>
            ))}
          </PopoverContent>
        </Popover>
      </div>
      <div
        role="group"
        aria-label={t.filterTitle}
        className="hidden flex-wrap items-center gap-2 md:flex"
      >
        <span className="mr-1 shrink-0 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {t.filterTitle}
        </span>
        {available.map((filter) => {
          const isActive = active.has(filter);
          return (
            <button
              key={filter}
              type="button"
              aria-pressed={isActive}
              onClick={() => onToggle(filter)}
              className={cn(
                "min-h-9 shrink-0 whitespace-nowrap rounded-md border px-3 py-1.5 text-sm font-medium transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring",
                isActive
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-card text-foreground hover:bg-muted"
              )}
            >
              {t.filters[filter]}
            </button>
          );
        })}
      </div>
      {activeCount > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          {t.filterCount(shownCount, totalCount)} ·{" "}
          <button
            type="button"
            onClick={onClear}
            className="underline underline-offset-2 hover:no-underline"
          >
            {t.clearFilters}
          </button>
        </p>
      )}
    </div>
  );
}
